import { MenuItem, Category, Order, OrderItem, WaiterRequest } from '../types';
import { httpClient, ApiError } from './http';
import { headerService } from './headers';
import { API_ENDPOINTS } from '../config/api';

// Mapeo de tipos del backend al frontend
const mapBackendMenuItem = (backendItem: any): MenuItem => ({
  id: backendItem.id,
  name: backendItem.name,
  description: backendItem.description,
  price: parseFloat(backendItem.price),
  category: backendItem.categoryName || backendItem.category || backendItem.categoryId || backendItem.category_id,
  categoryId: backendItem.categoryId || backendItem.category_id,
  categoryName: backendItem.categoryName,
  categoryIcon: backendItem.categoryIcon,
  categoryColor: backendItem.categoryColor,
  restaurantId: backendItem.restaurantId || backendItem.restaurant_id,
  restaurantName: backendItem.restaurantName,
  images: backendItem.images || [], // Mapear las imágenes del backend
  available: backendItem.available,
  allergens: backendItem.allergens || [],
  preparationTime: backendItem.preparationTime || backendItem.preparation_time,
  ingredients: backendItem.ingredients || [],
  nutritionalInfo: backendItem.nutritionalInfo || backendItem.nutritional_info || {},
  sizes: backendItem.sizes || [],
  status: backendItem.status,
});

const mapBackendCategory = (backendCategory: any): Category => ({
  id: backendCategory.id,
  name: backendCategory.name,
  icon: backendCategory.icon || '🍽️',
  color: backendCategory.color || '#FF6B6B',
});

const mapBackendOrder = (backendOrder: any): Order => {
  const rawItems = Array.isArray(backendOrder.items)
    ? backendOrder.items
    : (Array.isArray(backendOrder.items?.data)
        ? backendOrder.items.data
        : (Array.isArray(backendOrder.data?.items)
            ? backendOrder.data.items
            : []));

  const items = rawItems.map((item: any) => ({
    id: item.id,
    name: item.menuItemName || item.menu_item_name || item.name,
    quantity: item.quantity || 1,
    price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
    notes: item.notes,
    category: item.category || 'General',
  }));

  const parsedTotal = parseFloat(backendOrder.totalAmount || backendOrder.total_amount || 0);
  const computedTotal = items.reduce((sum: number, it: any) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
  const totalAmount = Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : computedTotal;

  return {
    id: backendOrder.id,
    restaurantId: backendOrder.restaurantId || backendOrder.restaurant_id,
    restaurantName: backendOrder.restaurantName || backendOrder.restaurant_name,
    items,
    totalAmount,
    status: backendOrder.status,
    orderDate: new Date(backendOrder.createdAt || backendOrder.created_at),
    estimatedDelivery: backendOrder.estimatedDelivery ? new Date(backendOrder.estimatedDelivery) : undefined,
    actualDelivery: backendOrder.actualDelivery ? new Date(backendOrder.actualDelivery) : undefined,
    deliveryAddress: backendOrder.deliveryAddress || backendOrder.delivery_address,
    paymentMethod: backendOrder.paymentMethod || backendOrder.payment_method || 'card',
    notes: backendOrder.notes,
    rating: backendOrder.rating,
    review: backendOrder.review,
  };
};

// API principal
export const api = {
  // Obtener categorías
  getCategories: async (restaurantId?: string): Promise<Category[]> => {
    const endpoint = restaurantId 
      ? `${API_ENDPOINTS.PUBLIC_CATEGORIES}?restaurantId=${restaurantId}`
      : API_ENDPOINTS.PUBLIC_CATEGORIES;
    
    console.log('🔄 Fetching categories from:', endpoint);
    const response = await httpClient.get<any>(endpoint);
    // Si la respuesta tiene estructura paginada, extraer los datos
    const categoriesData = response.data || response;
    return Array.isArray(categoriesData) ? categoriesData.map(mapBackendCategory) : [];
  },

  // Obtener menú completo
  getMenu: async (restaurantId?: string): Promise<MenuItem[]> => {
    const endpoint = restaurantId 
      ? `${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`
      : API_ENDPOINTS.MENU;
    
    console.log('🔄 Fetching menu from:', endpoint);
    const response = await httpClient.get<any>(endpoint);
    const menuData = response.data || response;
    return Array.isArray(menuData) ? menuData.map(mapBackendMenuItem) : [];
  },

  // Obtener menú por categoría
  getMenuByCategory: async (categoryId: string): Promise<MenuItem[]> => {
    const response = await httpClient.get<any>(API_ENDPOINTS.MENU_BY_CATEGORY(categoryId));
    const menuData = response.data || response;
    return Array.isArray(menuData) ? menuData.map(mapBackendMenuItem) : [];
  },

  // Crear nueva orden
  createOrder: async (orderData: {
    tableSigned?: string;
    items: Omit<OrderItem, 'id'>[];
    deliveryAddress?: string;
    notes?: string;
  }): Promise<Order> => {
    try {
      const { tableSigned, items, deliveryAddress, notes } = orderData;
      
      console.log('📋 Creando orden:', {
        hasTableSigned: !!tableSigned,
        itemsCount: items.length
      });

      const payload: any = {
        table_signed: tableSigned,
        items: items.map(item => ({
          menu_item_id: item.menuItem.id,
          quantity: item.quantity,
          notes: item.notes || item.specialInstructions,
          size: (item as any).size,
          price: item.price,
        })),
        payment_method: 'card', // Default payment method
        notes: notes?.trim() || '', // Notas globales del pedido
      };

      // Agregar dirección de entrega si se proporciona
      if (deliveryAddress) {
        payload.delivery_address = deliveryAddress;
      }

      // Enviar SIEMPRE token (autenticado). Si no hay, error 401 claro.
      if (!headerService.isAuthenticated()) {
        throw new ApiError('Debes iniciar sesión para crear una orden', 401);
      }
      const response = await httpClient.post<Order>(API_ENDPOINTS.PUBLIC_ORDERS, payload, 'authenticated');
      return mapBackendOrder(response);
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error('Error al crear la orden');
    }
  },

  // Obtener órdenes por mesa
  getOrdersByTable: async (userId: string, tableNumber: number): Promise<Order[]> => {
    try {
      // Como no hay endpoint específico para órdenes por mesa, obtenemos todas y filtramos
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('getOrdersByTable: Necesita restaurantId para funcionar correctamente', userId, tableNumber);
      return [];
    } catch (error) {
      console.error('Error fetching orders by table:', error);
      return [];
    }
  },

  // Obtener todas las órdenes (para cocina)
  getAllOrders: async (userId: string): Promise<Order[]> => {
    try {
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('getAllOrders: Necesita restaurantId para funcionar correctamente', userId);
      return [];
    } catch (error) {
      console.error('Error fetching all orders:', error);
      return [];
    }
  },

  // Solicitar mesero
  requestWaiter: async (tableNumber: number, requestType: WaiterRequest['requestType'], message?: string): Promise<WaiterRequest> => {
    try {
      // Como no hay endpoint específico para solicitudes de mesero, simulamos la respuesta
      const newRequest: WaiterRequest = {
        id: `request-${Date.now()}`,
        tableNumber,
        requestType,
        message: message || '',
        status: 'pending',
        createdAt: new Date(),
      };
      
      console.log('📞 Solicitud de mesero simulada:', newRequest);
      return newRequest;
    } catch (error) {
      console.error('Error requesting waiter:', error);
      throw new Error('Error al solicitar mesero');
    }
  },

  // Obtener solicitudes de mesero
  getWaiterRequests: async (): Promise<WaiterRequest[]> => {
    try {
      // Como no hay endpoint específico, retornamos array vacío
      console.log('📞 No hay endpoint para solicitudes de mesero, retornando array vacío');
      return [];
    } catch (error) {
      console.error('Error fetching waiter requests:', error);
      return [];
    }
  },

  // Marcar solicitud de mesero como atendida
  markWaiterRequestAttended: async (requestId: string): Promise<void> => {
    try {
      // Como no hay endpoint específico, solo loggeamos
      console.log('✅ Solicitud de mesero marcada como atendida:', requestId);
    } catch (error) {
      console.error('Error marking waiter request as attended:', error);
      throw new Error('Error al marcar solicitud como atendida');
    }
  },

  // Obtener restaurantes
  getRestaurants: async (): Promise<any[]> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_RESTAURANTS);
      const restaurantsData = response.data || response;
      return Array.isArray(restaurantsData) ? restaurantsData : [];
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      return [];
    }
  },

  // Obtener restaurante por ID
  getRestaurantById: async (restaurantId: string): Promise<any> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_RESTAURANT_BY_ID(restaurantId));
      return response;
    } catch (error) {
      console.error('Error fetching restaurant by ID:', error);
      throw new Error('Error al obtener restaurante');
    }
  },

  // Obtener token JWT de la mesa virtual (#0) para pedidos sin QR
  getVirtualTableToken: async (restaurantId: string): Promise<{ token: string; tableId?: string; tableNumber?: number }> => {
    const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_VIRTUAL_TABLE_TOKEN(restaurantId), undefined, 'authenticated');
    return response;
  },

  // Obtener restaurantes destacados
  getFeaturedRestaurants: async (): Promise<any[]> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_FEATURED_RESTAURANTS);
      const restaurantsData = response.data || response;
      return Array.isArray(restaurantsData) ? restaurantsData : [];
    } catch (error) {
      console.error('Error fetching featured restaurants:', error);
      return [];
    }
  },

  // Buscar restaurantes
  searchRestaurants: async (query: string): Promise<any[]> => {
    try {
      const response = await httpClient.get<any>(`${API_ENDPOINTS.PUBLIC_SEARCH_RESTAURANTS}?q=${encodeURIComponent(query)}`);
      const restaurantsData = response.data || response;
      return Array.isArray(restaurantsData) ? restaurantsData : [];
    } catch (error) {
      console.error('Error searching restaurants:', error);
      return [];
    }
  },

  // Health check
  healthCheck: async (): Promise<boolean> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.HEALTH_CHECK);
      return response.status === 'ok';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },

  // Obtener estadísticas (para admin)
  getStats: async (userId: string): Promise<any> => {
    try {
      // Como no hay endpoint específico, retornamos datos simulados
      console.log('📊 Obteniendo estadísticas simuladas para usuario:', userId);
      return {
        totalOrders: 0,
        totalRevenue: 0,
        averageOrderValue: 0,
        topItems: [],
        recentOrders: []
      };
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw new Error('Error al obtener estadísticas');
    }
  }
}; 